import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useFieldError, useT } from '@kmgestao/i18n';
import { AuthShell, Button, Input, Label } from '@kmgestao/ui';

import { Brand } from '../../components/Brand.js';
import { CaptchaField, type CaptchaFieldHandle } from '../../features/auth/CaptchaField.js';
import { mapAuthErrorKey } from '../../features/auth/mapAuthError.js';
import {
  resetPasswordRequestSchema,
  type ResetPasswordRequestInput,
} from '../../features/auth/schemas.js';
import { useRequestPasswordReset } from '../../features/auth/useResetPassword.js';

export function ResetPasswordPage() {
  const captchaRef = useRef<CaptchaFieldHandle>(null);
  const [sent, setSent] = useState(false);
  const request = useRequestPasswordReset();
  const { t } = useT('auth');
  const tError = useFieldError();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordRequestInput>({
    resolver: zodResolver(resetPasswordRequestSchema),
    defaultValues: { email: '', captchaToken: '' },
  });

  const emailError = tError(errors.email);
  const captchaError = tError(errors.captchaToken);
  const rootError = tError(errors.root);

  const onSubmit = handleSubmit((values) => {
    request.mutate(values, {
      onSuccess: () => setSent(true),
      onError: (error) => {
        captchaRef.current?.reset();
        setValue('captchaToken', '');
        setError('root', { message: mapAuthErrorKey(error) });
      },
    });
  });

  return (
    <AuthShell brand={<Brand onDark className="h-20 sm:h-24" />}>
      <div className="space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{t('reset.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('reset.subtitle')}</p>
        </div>

        {sent ? (
          <div
            role="status"
            className="rounded-md border border-success/30 bg-success/10 p-3 text-sm text-success-foreground"
          >
            {t('reset.success')}
          </div>
        ) : (
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <div className="space-y-2">
              <Label htmlFor="reset-email">{t('reset.email')}</Label>
              <Input
                id="reset-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder={t('reset.emailPlaceholder')}
                hasError={!!errors.email}
                {...register('email')}
              />
              {emailError ? (
                <p className="text-sm text-destructive" role="alert">
                  {emailError}
                </p>
              ) : null}
            </div>

            <CaptchaField
              ref={captchaRef}
              onVerify={(token) => setValue('captchaToken', token, { shouldValidate: true })}
              onExpire={() => setValue('captchaToken', '', { shouldValidate: true })}
              error={captchaError}
            />

            {rootError ? (
              <div
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
              >
                {rootError}
              </div>
            ) : null}

            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('reset.submitting') : t('reset.submit')}
            </Button>
          </form>
        )}

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-primary hover:underline">
            {t('reset.backToLogin')}
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
